import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFile } from "../../contexts/FileContext";
import { Heading } from "../../design/heading";
import LoadingView from "../../components/LoadingView";
import EditFileButton from "./components/EditFileButton";
import FileStatusBadge from "../../components/FileStatusBadge";
import DeleteFileButton from "./components/DeleteFileButton";
import FileViewer from "../../components/FileViewer";
import { useSeenState } from "../../contexts/SeenStateContext";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import { FileService } from "../../services/FileService";
import { Text } from "../../design/text";

const FileDetailPage: React.FC = () => {
  const { recordSeenState } = useSeenState();
  const { fileId } = useParams<{ fileId: string }>();
  const { file, setSelectedFile, error, loading } = useFile();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!fileId) return;
    setSelectedFile(fileId);
    recordSeenState({ fileId });
  }, [fileId]);

  useEffect(() => {
    const fetchFileUrl = async () => {
      if (file) {
        const url = await FileService.getDownloadURL(fileId!);
        setFileUrl(url);
      }
    };
    fetchFileUrl();
  }, [file]);

  if (loading.details) {
    return <LoadingView title="Loading File..." />;
  }
  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!file) {
    return <ErrorView error={new Error("File not found")} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: "Files", href: "/files" },
          { label: file.name, href: `/file/${file._id}` },
        ]}
      />

      {/* Main Content */}
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col w-full gap-6">
          {/* File Details Section */}
          <div className="w-full">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-2">
                <Heading>File Detail</Heading>
                {file.description && <Text>{file.description}</Text>}
              </div>
              <div className="flex gap-2">
                <EditFileButton file={file} />
                <DeleteFileButton
                  fileId={file._id}
                  onFileDeleted={() => navigate(-1)}
                />
              </div>
            </div>
          </div>

          {/* File Viewer */}
          {fileUrl && <FileViewer fileUrl={fileUrl} />}
        </div>
      </div>
    </div>
  );
};

export default FileDetailPage;
